import type {
  ConfigurationImpactAssessment,
  ConfigurationImpactMode,
  DocumentTypeAdmin,
  EquipmentPackageRevision,
  EquipmentTypeAdmin,
  InspectionSiteAdmin,
} from './admin-api';

export function directoryStatusLabel(status: string): string {
  if (status === 'ACTIVE') {
    return 'Активен';
  }
  if (status === 'ARCHIVED') {
    return 'В архиве';
  }
  return status;
}

export function documentTypeBusinessLabel(
  row: Pick<DocumentTypeAdmin, 'code' | 'display_name'>,
): string {
  const name = row.display_name.trim();
  return name.length > 0 ? name : 'Тип документа без названия';
}

export function equipmentTypeBusinessLabel(
  row: Pick<EquipmentTypeAdmin, 'code' | 'display_name'>,
): string {
  const name = row.display_name.trim();
  return name.length > 0 ? name : 'Вид техники без названия';
}

export function inspectionSiteBusinessLabel(
  row: Pick<InspectionSiteAdmin, 'code' | 'display_name'>,
): string {
  const name = row.display_name.trim();
  return name.length > 0 ? name : 'Площадка без названия';
}

export function packageRevisionBusinessLabel(row: EquipmentPackageRevision): string {
  const current = row.is_current ? ' · текущая' : '';
  return `Версия набора ${row.revision_number}${current}`;
}

export function nextDisplayOrder(
  rows: readonly { readonly display_order: number }[] | null | undefined,
): number {
  if (rows === null || rows === undefined || rows.length === 0) {
    return 1;
  }
  let max = 0;
  for (const row of rows) {
    if (typeof row.display_order === 'number' && Number.isFinite(row.display_order)) {
      max = Math.max(max, row.display_order);
    }
  }
  return max + 1;
}

export function impactModeLabel(mode: ConfigurationImpactMode | string): string {
  if (mode === 'STANDARD') {
    return 'Стандартный';
  }
  if (mode === 'MANDATORY_PRESTART') {
    return 'Обязательный до старта';
  }
  return mode;
}

export function runtimeReadinessLabel(row: EquipmentTypeAdmin): string {
  if (row.runtime_readiness.selectable) {
    return 'Готов к выбору';
  }
  return 'Не готов к выбору';
}

const READINESS_REASON_LABELS: Record<string, string> = {
  NO_CURRENT_PACKAGE: 'Нет текущего набора чек-листов для вида техники',
  EMPTY_PACKAGE: 'Текущий набор чек-листов пуст',
  MISSING_ACTIVE_TEMPLATE_VERSION: 'Нет активной утверждённой версии чек-листа',
  STATUS_NOT_ACTIVE: 'Вид техники не активен',
};

export function runtimeReadinessReasonLabels(row: EquipmentTypeAdmin): readonly string[] {
  if (row.runtime_readiness.selectable) {
    return [];
  }
  const details = row.runtime_readiness.problem_details;
  const reasonsRaw =
    details !== null &&
    details !== undefined &&
    Array.isArray((details as { reasons?: unknown }).reasons)
      ? (details as { reasons: unknown[] }).reasons
      : [];
  const mapped = reasonsRaw
    .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
    .map((code) => READINESS_REASON_LABELS[code] ?? null)
    .filter((label): label is string => label !== null);
  if (mapped.length > 0) {
    return mapped;
  }
  if (row.runtime_readiness.problem_code === 'EQUIPMENT_TYPE_NOT_READY') {
    return ['Вид техники не готов к выбору подрядчиком'];
  }
  if (
    row.runtime_readiness.problem_code !== null &&
    row.runtime_readiness.problem_code !== undefined &&
    row.runtime_readiness.problem_code.trim() !== ''
  ) {
    return [];
  }
  return [];
}

export function impactAssessmentSummary(assessment: ConfigurationImpactAssessment): string {
  const c = assessment.counts;
  return [
    `Новые: ${c.new_cases}`,
    `Черновики: ${c.draft_cases}`,
    `Готовы/не начаты: ${c.ready_not_started_cases}`,
    `В работе: ${c.in_progress_cases}`,
    `Финализированы: ${c.finalized_cases}`,
    `Предзагружены: ${c.preloaded_cases}`,
  ].join(' · ');
}

import {
  defaultValidationRuleV1,
  isBlankValidation,
  isGrammarV1Validation,
  serializeValidationRuleV1,
} from './cfg-val-001';

const CONTROLLED_FIELD_KEYS = new Set([
  'field_code',
  'label',
  'value_type',
  'required_rule',
  'applicability_rule',
  'validation',
  'ordinal',
  'help_text',
  'required',
]);

export function emptyMetadataSchema(): Record<string, unknown> {
  return { fields: [] };
}

/** Count of metadata fields from a DocumentType schema payload (client-side only). */
export function countMetadataFields(schema: Record<string, unknown> | null | undefined): number {
  if (schema === null || schema === undefined) {
    return 0;
  }
  const fields = schema.fields;
  return Array.isArray(fields) ? fields.length : 0;
}

export function defaultRequiredRule(required: boolean): Record<string, unknown> {
  return { version: 1, type: required ? 'ALWAYS' : 'OPTIONAL' };
}

export function defaultApplicabilityRule(): Record<string, unknown> {
  return { version: 1, type: 'ALWAYS' };
}

export type MetadataFieldDraft = {
  readonly field_code: string;
  readonly label: string;
  readonly value_type: string;
  readonly required_rule: Record<string, unknown>;
  readonly applicability_rule: Record<string, unknown>;
  readonly validation: Record<string, unknown>;
  readonly ordinal: number;
  readonly help_text: string;
  /** Unknown/unsupported keys from server — preserved on save. */
  readonly preserved: Record<string, unknown>;
};

export type MetadataSchemaDraft = {
  readonly fields: MetadataFieldDraft[];
  readonly preservedRoot: Record<string, unknown>;
};

function asObject(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function requiredRuleFromField(row: Record<string, unknown>): Record<string, unknown> {
  const rule = asObject(row.required_rule);
  if (rule !== null) {
    return { ...rule };
  }
  if (row.required === true) {
    return defaultRequiredRule(true);
  }
  if (row.required === false) {
    return defaultRequiredRule(false);
  }
  return defaultRequiredRule(false);
}

function isRequiredRule(rule: Record<string, unknown>): boolean {
  return rule.type === 'ALWAYS' || rule.type === 'WHEN_APPLICABLE';
}

export function metadataFieldIsRequired(field: MetadataFieldDraft): boolean {
  return isRequiredRule(field.required_rule);
}

export function emptyMetadataField(ordinal: number): MetadataFieldDraft {
  return {
    field_code: `field_${ordinal}`,
    label: '',
    value_type: 'STRING',
    required_rule: defaultRequiredRule(false),
    applicability_rule: defaultApplicabilityRule(),
    validation: { version: 1 },
    ordinal,
    help_text: '',
    preserved: {},
  };
}

export function metadataSchemaFromServer(schema: Record<string, unknown>): MetadataSchemaDraft {
  const preservedRoot: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(schema)) {
    if (key !== 'fields') {
      preservedRoot[key] = value;
    }
  }
  const fieldsRaw = schema.fields;
  if (!Array.isArray(fieldsRaw)) {
    return { fields: [], preservedRoot };
  }
  const fields: MetadataFieldDraft[] = [];
  fieldsRaw.forEach((item, index) => {
    const row = asObject(item);
    if (row === null) {
      return;
    }
    const fieldCode =
      typeof row.field_code === 'string'
        ? row.field_code
        : typeof row.code === 'string'
          ? row.code
          : '';
    if (fieldCode.length === 0) {
      return;
    }
    const preserved: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      if (!CONTROLLED_FIELD_KEYS.has(key) && key !== 'code') {
        preserved[key] = value;
      }
    }
    const ordinal =
      typeof row.ordinal === 'number' && Number.isFinite(row.ordinal) && row.ordinal >= 1
        ? Math.trunc(row.ordinal)
        : index + 1;
    fields.push({
      field_code: fieldCode,
      label: typeof row.label === 'string' ? row.label : fieldCode,
      value_type: typeof row.value_type === 'string' ? row.value_type : 'STRING',
      required_rule: requiredRuleFromField(row),
      applicability_rule: asObject(row.applicability_rule) ?? defaultApplicabilityRule(),
      validation: asObject(row.validation) ?? {},
      ordinal,
      help_text: typeof row.help_text === 'string' ? row.help_text : '',
      preserved,
    });
  });
  // Presentation order by ordinal; do NOT rewrite authoritative ordinals on load.
  fields.sort((a, b) => a.ordinal - b.ordinal || a.field_code.localeCompare(b.field_code));
  return {
    fields,
    preservedRoot,
  };
}

/** @deprecated Prefer metadataSchemaFromServer — kept for callers that only need fields. */
export function metadataFieldsFromSchema(schema: Record<string, unknown>): MetadataFieldDraft[] {
  return metadataSchemaFromServer(schema).fields;
}

export function reindexMetadataOrdinals(
  fields: readonly MetadataFieldDraft[],
): MetadataFieldDraft[] {
  return fields.map((row, index) => ({ ...row, ordinal: index + 1 }));
}

export function schemaFromMetadataDraft(draft: MetadataSchemaDraft): Record<string, unknown> {
  return {
    ...draft.preservedRoot,
    fields: draft.fields.map((row) => {
      let validation: Record<string, unknown>;
      if (isBlankValidation(row.validation)) {
        validation = defaultValidationRuleV1(row.value_type);
      } else if (isGrammarV1Validation(row.validation)) {
        validation = serializeValidationRuleV1(row.value_type, row.validation);
      } else {
        validation = { ...row.validation };
      }
      return {
        ...row.preserved,
        field_code: row.field_code,
        label: row.label,
        value_type: row.value_type,
        required_rule: { ...row.required_rule },
        applicability_rule: { ...row.applicability_rule },
        validation,
        ordinal: row.ordinal,
        help_text: row.help_text.trim() === '' ? null : row.help_text,
      };
    }),
  };
}

/** @deprecated Prefer schemaFromMetadataDraft. */
export function schemaFromMetadataFields(
  fields: readonly MetadataFieldDraft[],
): Record<string, unknown> {
  return schemaFromMetadataDraft({ fields: reindexMetadataOrdinals(fields), preservedRoot: {} });
}

export function requiredRuleTypeOptions(): readonly {
  readonly value: string;
  readonly label: string;
}[] {
  return [
    { value: 'ALWAYS', label: 'Всегда обязательно' },
    { value: 'WHEN_APPLICABLE', label: 'Обязательно при применимости' },
    { value: 'OPTIONAL', label: 'Необязательно' },
  ];
}

export function applicabilityRuleTypeOptions(): readonly {
  readonly value: string;
  readonly label: string;
}[] {
  return [
    { value: 'ALWAYS', label: 'Всегда применимо' },
    { value: 'INSPECTOR_DECLARED', label: 'По решению инспектора' },
  ];
}

export function isSimpleApplicabilityRule(rule: Record<string, unknown>): boolean {
  return rule.type === 'ALWAYS' || rule.type === 'INSPECTOR_DECLARED';
}

export function validationSummary(validation: Record<string, unknown>): string {
  const keys = Object.keys(validation);
  if (keys.length === 0) {
    return 'Validation v1: дополнительные ограничения не заданы';
  }
  if (validation.version === 1) {
    return `Validation v1 (${keys.filter((key) => key !== 'version').join(', ') || 'только тип'})`;
  }
  return `Дополнительное правило сохранено без изменений (${keys.join(', ')})`;
}

export const METADATA_VALUE_TYPE_OPTIONS: readonly {
  readonly value: string;
  readonly label: string;
}[] = [
  { value: 'STRING', label: 'Короткий текст' },
  { value: 'TEXT', label: 'Многострочный текст' },
  { value: 'NUMBER', label: 'Число' },
  { value: 'DATE', label: 'Дата' },
  { value: 'DATETIME', label: 'Дата и время' },
  { value: 'ENUM', label: 'Выбор из списка' },
  { value: 'REF_CONTRACTOR', label: 'Организация' },
  { value: 'REF_VEHICLE', label: 'Транспортное средство' },
  { value: 'REF_PERSON', label: 'Человек / сотрудник' },
];
